// upsertFullMenuHelpers.ts
import * as yup from 'yup';

// =================================================================
// Step 1: Define the "Universe" of all possible items (Inventory)
// =================================================================

export const ingredientOptionSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Option name is required"),
  price: yup.number().min(0).required("Price adjustment is required (can be 0)"),
  isSoldOut: yup.boolean().required().default(false)
});
export type IngredientOption = yup.InferType<typeof ingredientOptionSchema>;

/** Individual variant (leaf) */
export const itemVariantSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Variant name is required (e.g., 'Small')"),
  price: yup.number().min(0).required("Price is required for each variant"),
  comboPrice: yup.number().min(0).required("Combo price adjustment is required (can be 0)"),
  isSoldOut: yup.boolean().required().default(false)
});
export type ItemVariant = yup.InferType<typeof itemVariantSchema>;

/** Group of variants (e.g., "Wrap type" or "Size") */
export const baseItemVariantsGroupSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Variant group name is required"),
  description: yup.string().required("Variant group description is required"),
  variants: yup.array(itemVariantSchema).min(1, "Variant group must contain at least one variant").required(),
});
export type BaseItemVariants = yup.InferType<typeof baseItemVariantsGroupSchema>;

/** Group of options (e.g., "Extras", "Toppings") */
export const baseItemOptionsGroupSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Option group name is required"),
  description: yup.string().required("Option group description is required"),
  options: yup.array(ingredientOptionSchema).required(),
});
export type BaseItemOptions = yup.InferType<typeof baseItemOptionsGroupSchema>;

/** Base item now contains variant groups and option groups */
export const baseItemSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Item name is required"),
  price: yup.number().min(0).required("Item price is required"),
  imageUrl: yup.string().optional(),
  isSoldOut: yup.boolean().required().default(false),
  includedInMetrics: yup.boolean().default(false),
  variants: yup.array(baseItemVariantsGroupSchema).min(1, "Must have at least one variant group").required(),
  options: yup.array(baseItemOptionsGroupSchema).required(),
});
export type BaseItem = yup.InferType<typeof baseItemSchema>;

// =================================================================
// Step 2: Define the structure of the customer-facing Menu
// =================================================================

export const comboVariantSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Combo variant name is required (e.g., 'Large')"),
  price: yup.number().min(0).required("A specific price for the combo variant is required"),
  imageUrl: yup.string().optional(),
  itemVariantIds: yup.array(yup.string().required()).min(1, "A combo variant must include at least one item").required(),
});
export type ComboVariant = yup.InferType<typeof comboVariantSchema>;

export const menuItemSchema = yup.object({
  id: yup.string().required(),
  baseItemId: yup.string().required("Each menu item must link to a base item"),
  isOnSale: yup.boolean().default(false),
  salePrice: yup.number().min(0).when('isOnSale', {
    is: true,
    then: (schema) => schema.required("Sale price is required when item is on sale"),
    otherwise: (schema) => schema.optional(),
  }),
  isCombo: yup.boolean().default(false),
  imageUrl: yup.string().optional(),
  comboVariants: yup.array(comboVariantSchema).when('isCombo', {
    is: true,
    then: (schema) => schema.min(1, "A combo must have at least one variant (e.g., 'Regular Meal')"),
    otherwise: (schema) => schema.optional(),
  }),
});
export type MenuItem = yup.InferType<typeof menuItemSchema>;

export const menuCategorySchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Category name is required"),
  items: yup.array(menuItemSchema).required(),
});
export type MenuCategory = yup.InferType<typeof menuCategorySchema>;

// New: Defines a price override for a specific variant within a bundle.
export const variantPriceOverrideSchema = yup.object({
  id: yup.string().required(),
  variantId: yup.string().required(),
  price: yup.number().min(0).required("A bundle-specific price is required for the variant"),
});
export type VariantPriceOverride = yup.InferType<typeof variantPriceOverrideSchema>;

// New: Defines an item included in a bundle, with quantity and price overrides.
export const bundleItemSchema = yup.object({
  id: yup.string().required(),
  menuItemId: yup.string().required("You must select a menu item"),
  quantity: yup.number().min(1).required("Quantity is required"),
  noVariantPriceOverride: yup.number().min(0).optional(),
  variantPriceOverrides: yup.array(variantPriceOverrideSchema).required(),
});
export type BundleItem = yup.InferType<typeof bundleItemSchema>;

// Updated: A bundle is now a container for items with special pricing.
export const bundleSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required("Bundle name is required"),
  description: yup.string().optional(),
  imageUrl: yup.string().optional(),
  items: yup.array(bundleItemSchema).min(1, "A bundle must contain at least one item").required(),
});
export type Bundle = yup.InferType<typeof bundleSchema>;

// =================================================================
// Top-Level Schema for the entire page's data
// =================================================================

export const fullMenuPageSchema = yup.object({
  baseItems: yup.array(baseItemSchema).required(),
  menu: yup.object({
    categories: yup.array(menuCategorySchema).required(),
    bundles: yup.array(bundleSchema).required(),
  }).required(),
});
export type FullMenuPageData = yup.InferType<typeof fullMenuPageSchema>;

// =================================================================
// Example initial data (updated to nested groups)
// =================================================================

export const initialData: FullMenuPageData | undefined = {
  menu: {
    bundles: [
      {
        items: [
          {
            variantPriceOverrides: [
              { price: 15, variantId: "2025-07-18T07:34:58.047Z", id: "2025-07-18T08:04:13.188Z" },
              { price: 25, variantId: "2025-07-18T08:03:05.553Z", id: "2025-07-18T08:04:13.189Z" },
              { price: 30, variantId: "2025-07-18T08:03:22.693Z", id: "2025-07-18T08:04:13.190Z" }
            ],
            quantity: 2,
            menuItemId: "2025-07-18T07:34:48.708Z",
            id: "2025-07-18T08:04:10.330Z"
          }
        ],
        description: "2 burgers",
        name: "2sday",
        id: "2025-07-18T08:03:59.331Z"
      }
    ],
    categories: [
      {
        items: [
          {
            isCombo: true,
            comboVariants: [
              {
                itemVariantIds: [
                  "2025-07-18T07:33:36.631Z",
                  "2025-07-18T08:01:55.325Z",
                  "2025-07-22T05:58:54.153Z"
                ],
                price: 30,
                name: "Standard",
                id: "2025-07-22T05:59:48.107Z"
              }
            ],
            isOnSale: false,
            baseItemId: "2025-07-18T07:33:36.631Z",
            id: "2025-07-18T07:34:48.708Z"
          }
        ],
        name: "burger",
        id: "2025-07-18T07:34:44.537Z"
      }
    ]
  },
  baseItems: [
    {
      id: "2025-07-18T07:33:36.631Z",
      name: "Burger",
      price: 30,
      includedInMetrics: false,
      isSoldOut: false,
      variants: [
        {
          id: "vg-1-2025-07-18T07:33:36.631Z",
          name: "Default Group",
          description: "",
          variants: [
            {
              id: "2025-07-18T07:33:36.631Z",
              name: "Standard",
              price: 30,
              comboPrice: 123,
              isSoldOut: false
            }
          ]
        }
      ],
      options: []
    },
    {
      id: "2025-07-18T08:01:55.325Z",
      name: "Fries",
      price: 4,
      includedInMetrics: false,
      isSoldOut: false,
      variants: [
        {
          id: "vg-1-2025-07-18T08:01:55.325Z",
          name: "Default Group",
          description: "",
          variants: [
            {
              id: "2025-07-18T08:01:55.325Z",
              name: "Standard",
              price: 10,
              comboPrice: 32,
              isSoldOut: false
            },
            {
              id: "2025-07-18T08:02:02.504Z",
              name: "Large",
              price: 12,
              comboPrice: 21,
              isSoldOut: false
            }
          ]
        }
      ],
      options: []
    },
    {
      id: "2025-07-22T05:58:54.153Z",
      name: "Soda",
      includedInMetrics: false,
      price: 2,
      isSoldOut: false,
      variants: [
        {
          id: "vg-1-2025-07-22T05:58:54.153Z",
          name: "Default Group",
          description: "",
          variants: [
            {
              id: "2025-07-22T05:58:54.153Z",
              name: "Coke",
              price: 12,
              comboPrice: 2,
              isSoldOut: false
            },
            {
              id: "2025-07-22T05:59:04.418Z",
              name: "PepsiMax",
              price: 10,
              comboPrice: 0,
              isSoldOut: false,
            }
          ]
        }
      ],
      options: []
    }
  ]
};
